<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import Skeleton from '@/components/Skeleton.vue'
import CreateAccount from '@/components/Register/CreateAccount.vue'
import NameYourOrg from '@/components/Register/NameYourOrg.vue'
import ChooseExam from '@/components/Register/ChooseExam.vue'
import { userModule } from '@/store/user/module'
import { organizationModule } from '@/store/organization/module'
import { instructorModule } from '@/store/instructor/module'
import { examModule } from '@/store/exam/module'
import { groupModule } from '@/store/group/module'
import { analyticsModule } from '@/store/analytics/module'
import type { UTMProperties } from '@/store/user/actions'
import type { Study } from '@pocketprep/types'
import * as Sentry from '@sentry/vue'
import BundleExam from '@/components/Register/ChooseExam/BundleExam.vue'
import MobileWarning from '@/components/MobileWarning.vue'
import { screenModule } from '@/store/screen/module'

export type TBundleExam = Study.Class.ExamMetadataJSON & { bundle: Study.Class.BundleJSON }

const {
    ModalContainer,
    Icon,
    Input: PocketInput,
    Button: PocketButton,
    PhonePerson,
} = UIKit

const route = useRoute()
const router = useRouter()

const currentStep = ref(1)
const maxStep = ref(1)
const isLoading = ref(false)
const createAccountErrorMessage = ref('')
const lastUpdatedHubspotLead = ref<null | Date>(null)

// Exam selection
const selectedExam = ref<null | TBundleExam>(null)
const selectedBundle = ref<null | Study.Class.BundleJSON>(null)
const searchVal = ref('')
const examDisplayName = ref('')

const user = computed(() => {
    return userModule.state.user
})

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

onMounted(async () => {
    await Promise.all([
        groupModule.actions.fetchCurrentOrgGroups(),
        examModule.actions.fetchExamMetadata(),
    ])
    // if user is already registered and onboarding completed, redirect to stats
    const orgGroups = groupModule.getters.getCurrentOrgGroups()
    if (Object.keys(orgGroups).length) {
        router.push({ name: 'stats', query: { ...route.query } })
    }
})

const selectBundle = (bundle: Study.Class.BundleJSON) => {
    selectedBundle.value = bundle
}

const selectExam = (exam: TBundleExam) => {
    selectedExam.value = exam
    examDisplayName.value = exam.nativeAppName
}

const clearSelectedExam = () => {
    selectedExam.value = null
}

const search = (value: string) => {
    searchVal.value = value
}

// Hubspot has a 5-10 second delay between adding a new record and it being returned in searches.
// This results in an error if a user goes through the onboarding steps too quickly. To fix that,
// we're just going to impose a 11 second delay between lead update calls. This should be fine because
// we don't need to await this function. Also there will be no delay when the first update happens and
// saves the contact lead which is by far the most important step.
const runHubspotLeadUpdate = async (params: { organizationId?: string; utm?: UTMProperties }) => {
    const delayTimeMs = (lastUpdatedHubspotLead.value?.getTime() || 0) - (new Date().getTime() - 11000)

    if (delayTimeMs > 0) {
        lastUpdatedHubspotLead.value?.setMilliseconds(lastUpdatedHubspotLead.value.getMilliseconds() + delayTimeMs)
        await new Promise(res => setTimeout(() => res(undefined), delayTimeMs))
    } else {
        lastUpdatedHubspotLead.value = new Date()
    }

    await userModule.actions.upsertHubspotLead(params)
}

const submitCreateAccount = async (
    formData: ReturnType<typeof CreateAccount['submitCreateAccount']>
) => {
    createAccountErrorMessage.value = ''
    isLoading.value = true
    const password =
        (formData.signInWithPassword && formData.password) || generatePassword()

    try {
        if (!user.value) {
            await userModule.actions.createUser({
                username: formData.email,
                password,
                firstName: formData.firstName,
                lastName: formData.lastName,
            })
        } else {
            await userModule.actions.updateUser({
                firstName: formData.firstName,
                lastName: formData.lastName,
            })
        }
        // Send onboarding step 1 complete event to Amplitude
        analyticsModule.actions.amplitudeTrack('Onboarding Step Completed', {
            onboardingStepName: 'accountCreated',
        })

        // Upsert into hubspot as a lead
        const upsertLeadPayload: {
            organizationId?: string
            utm?: UTMProperties
        } = {
            organizationId: currentOrganization.value?.objectId,
        }

        if (route.query.utm_campaign) {
            upsertLeadPayload.utm = {
                campaign: (route.query.utm_campaign as string) || '',
                content: (route.query.utm_content as string) || '',
                medium: (route.query.utm_medium as string) || '',
                source: (route.query.utm_source as string) || '',
                term: (route.query.utm_term as string) || '',
            }
        }

        runHubspotLeadUpdate(upsertLeadPayload)

        // Increment onboarding step
        currentStep.value++
        if (maxStep.value === 1) {
            maxStep.value = 2
        }
    } catch (err) {
        if (err instanceof Error && err.message === 'Account already exists for this username.') {
            createAccountErrorMessage.value = 'Account already exists for this email.'
        } else if (err instanceof Error) {
            createAccountErrorMessage.value = err.message
        }
    }

    isLoading.value = false
}

const submitNameYourOrg = async (
    formData: ReturnType<typeof NameYourOrg['submitNameYourOrg']>
) => {
    if (!user.value) {
        throw new Error('Register.submitNameYourOrg: Could not find user')
    }
    isLoading.value = true

    if (!currentOrganization.value) {
        await organizationModule.actions.addOrganization({ name: formData.orgName })
    } else {
        await organizationModule.actions.updateOrganization(
            { organizationId: currentOrganization.value.objectId, name: formData.orgName }
        )
    }

    currentStep.value++
    if (maxStep.value === 2) {
        maxStep.value = 3
    }

    // Send onboarding step 2 completed event to Amplitude
    analyticsModule.actions.amplitudeTrack('Onboarding Step Completed', {
        // Safe coercion since we know that it'll be defined here
        organizationId: currentOrganization.value?.objectId as string,
        organizationName: currentOrganization.value?.name as string,
        onboardingStepName: 'orgNamed',
    })

    // Upsert into hubspot as a lead
    runHubspotLeadUpdate({ organizationId: currentOrganization.value?.objectId })
    isLoading.value = false
}

const submitChooseExam = async () => {
    if (!user.value) {
        throw new Error('Register.submitChooseExam: Could not find user')
    }
    if (!currentOrganization.value) {
        throw new Error('Register.submitChooseExam: Could not find current organization')
    }
    if (!currentOrgInstructor.value) {
        throw new Error('Register.submitChooseExam: Could not find current org instructor')
    }
    isLoading.value = true

    if (!selectedExam.value) {
        return
    }

    const createdOrgExam = await examModule.actions.addOrgExam({
        organizationId: currentOrganization.value.objectId,
        examMetadataId: selectedExam.value.objectId,
        displayName: examDisplayName.value,
    })
    const createdExamGroup = await groupModule.actions.addGroup({
        organizationId: currentOrganization.value.objectId,
        name: examDisplayName.value,
        type: 'system',
        orgExamId: createdOrgExam.objectId,
        orgStudentIds: [],
        isPubliclyEditable: false,
    })
    await groupModule.actions.addGroup({
        organizationId: currentOrganization.value.objectId,
        name: `${user.value.lastName} - ${examDisplayName.value}`,
        type: 'system',
        orgExamId: createdOrgExam.objectId,
        ownerId: currentOrgInstructor.value.objectId,
        orgStudentIds: [],
        isPubliclyEditable: false,
    })
    await instructorModule.actions.updateVisibleOrgGroups([ createdExamGroup.objectId ])

    // Create UserExamMetadata and update the user's currentExamGuid to be a valid student as well
    try {
        if (!user.value.currentExamGuid) {
            await Promise.all([
                userModule.actions.createUserExamMetadata({
                    examGuid: selectedExam.value.examGuid,
                    examVersion: selectedExam.value.version,
                }),
                userModule.actions.updateUser({
                    currentExamGuid: selectedExam.value.examGuid,
                }),
            ])
        }

        // Send onboarding step 3 completed event to Amplitude
        analyticsModule.actions.amplitudeTrack('Onboarding Step Completed', {
            examGuid: selectedExam.value.examGuid,
            examName: examDisplayName.value,
            onboardingStepName: 'examSelected',
            organizationId: currentOrganization.value.objectId,
            organizationName: currentOrganization.value.name,
        })

        // Upsert into hubspot as a lead
        runHubspotLeadUpdate({ organizationId: currentOrganization.value?.objectId })
    } catch (err) {
        // Handle as a soft failure - Teach will work without this succeeding
        const processedError = typeof err === 'string' ? new Error(err) : err
        Sentry.captureException(processedError)
    }

    // Once onboarding is complete, we should set their last active app
    await userModule.actions.updateWebConfig({ lastActiveApp: 'teach' })

    await userModule.actions.trackEvent('Onboarding_Completed', {
        examName: selectedExam.value.nativeAppName,
    })

    isLoading.value = false

    router.push({
        name: 'stats',
    })
}

const clickPreviousStep = () => {
    currentStep.value--
}

const clickStep = (stepNum: number) => {
    if (stepNum === 1 && currentStep.value !== 1 && maxStep.value >= 1) {
        currentStep.value = 1
    } else if (stepNum === 2 && currentStep.value !== 2 && maxStep.value >= 2) {
        currentStep.value = 2
    } else if (stepNum === 3 && currentStep.value !== 3 && maxStep.value === 3) {
        currentStep.value = 3
    }
}

const generatePassword = (): string => {
    const length = 12
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let retVal = ''
    for (let i = 0, n = charset.length; i < length; ++i) {
        retVal += charset.charAt(Math.floor(Math.random() * n))
    }
    return retVal
}
</script>

<template>
    <Skeleton class="register">
        <Portal to="modal">
            <ModalContainer key="registerModal">
                <div class="register__mobile-warning">
                    <MobileWarning
                        v-if="breakpoint === 'black-bear'"
                    />
                </div>
                <div class="register__stepper">
                    <Icon
                        v-if="currentStep > 1"
                        class="register__previous-step"
                        type="arrow"
                        @click="clickPreviousStep"
                    />
                    <span
                        class="register__step"
                        :class="{
                            'register__step--current': currentStep === 1,
                            'register__step--complete': currentStep > 1,
                            'register__step--clickable': maxStep >= 1 && currentStep !== 1,
                        }"
                        @click="clickStep(1)"
                    >
                        <span
                            v-if="currentStep <= 1"
                            class="register__step-label-number"
                        >
                            1
                        </span>
                        <Icon
                            v-else
                            class="register__step-label-check"
                            type="check"
                        />
                        <span>Create Account</span>
                    </span>
                    <Icon type="paginationArrow" class="register__stepper-arrow" />
                    <span
                        class="register__step"
                        :class="{
                            'register__step--current': currentStep === 2,
                            'register__step--complete': currentStep > 2,
                            'register__step--clickable': maxStep >= 2 && currentStep !== 2,
                        }"
                        @click="clickStep(2)"
                    >
                        <span
                            v-if="currentStep <= 2"
                            class="register__step-label-number"
                        >
                            2
                        </span>
                        <Icon
                            v-else
                            class="register__step-label-check"
                            type="check"
                        />
                        <span>Name Your Organization</span>
                    </span>
                    <Icon type="paginationArrow" class="register__stepper-arrow" />
                    <span
                        class="register__step"
                        :class="{
                            'register__step--current': currentStep === 3,
                            'register__step--complete': currentStep > 3,
                            'register__step--clickable': maxStep === 3 && currentStep !== 3,
                        }"
                        @click="clickStep(3)"
                    >
                        <span
                            v-if="currentStep <= 3"
                            class="register__step-label-number"
                        >
                            3
                        </span>
                        <Icon
                            v-else
                            class="register__step-label-check"
                            type="check"
                        />
                        <span>Choose Exam</span>
                    </span>
                </div>
                <div class="register__step-container">
                    <CreateAccount
                        v-show="currentStep === 1"
                        :is-loading="isLoading"
                        :error-message="createAccountErrorMessage"
                        @submitCreateAccount="submitCreateAccount"
                    />
                    <NameYourOrg
                        v-show="currentStep === 2"
                        :is-loading="isLoading"
                        @submitNameYourOrg="submitNameYourOrg"
                    />
                    <div
                        v-if="currentStep === 3"
                        class="register__step3"
                    >
                        <PhonePerson
                            v-if="selectedExam && selectedBundle?.objectId"
                            class="register__phone-person"
                            :bundle-id="selectedBundle?.objectId"
                        />
                        <div class="register__step-text">
                            Step 3 of 3
                        </div>
                        <div class="register__text-big">
                            What is one exam your students are preparing for?
                        </div>
                        <div class="register__text-small">
                            You’ll be able to add more exams later
                        </div>
                        <ChooseExam
                            v-if="!selectedExam"
                            :is-loading="isLoading"
                            @select="selectExam"
                            @selectBundle="selectBundle"
                            @search="search"
                        />
                        <div
                            v-else-if="selectedExam"
                            class="register__confirmation"
                        >
                            <div class="register__selected-exam-container">
                                <BundleExam
                                    class="register__selected-exam"
                                    tabindex="-1"
                                    :exam="selectedExam"
                                />
                                <Icon
                                    class="register__unselect-exam-icon"
                                    type="close"
                                    tabindex="0"
                                    @click="clearSelectedExam"
                                    @keyup.enter="clearSelectedExam"
                                />
                            </div>
                            <div>
                                <div class="register__display-name-prompt">
                                    Awesome, do you call this exam something different in normal conversation?
                                </div>
                                <div class="register__display-name-hint">
                                    You can edit the display name to match what’s natural to you.
                                </div>
                                <PocketInput
                                    v-model="examDisplayName"
                                    class="register__display-name-input"
                                    type="text"
                                    label="Display Name"
                                />
                            </div>
                            <PocketButton
                                class="register__submit"
                                :disabled="!examDisplayName || isLoading"
                                @click="submitChooseExam"
                            >
                                Add {{ examDisplayName }}
                            </PocketButton>
                        </div>
                    </div>
                </div>
            </ModalContainer>
        </Portal>
    </Skeleton>
</template>

<style lang="scss" scoped>
@use 'sass:color';

.register {
    &__stepper {
        position: relative;
        width: 964px;
        height: 56px;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 13px;
        background-color: white;
        color: $slate-01;
    }

    &__previous-step {
        color: $brand-blue;
        position: absolute;
        left: 20px;
        top: 50%;
        transform: translateY(-50%) rotate(180deg) scale(1.5);
        cursor: pointer;

        &:hover {
            color: $brand-black;
        }
    }

    &__step {
        position: relative;
        display: flex;
        align-items: center;

        &--current {
            color: $brand-black;
            font-weight: 600;

            .register__step-label-number {
                color: $brand-blue;
            }
        }

        &--complete {
            text-decoration: line-through;
        }

        &--clickable {
            cursor: pointer;
        }
    }

    &__step-label-number {
        position: absolute;
        left: -15px;
        font-weight: 600;
    }

    &__step-label-check {
        position: absolute;
        left: -19px;
        width: 15px;
        height: 15px;
        top: -2px;
    }

    &__stepper-arrow {
        height: 8px;
        width: 8px;
        margin-left: 12px;
        margin-right: 28px;
    }

    &__step3 {
        padding: 26px;
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    &__phone-person {
        position: absolute;
        right: 0;
        bottom: 0;
        width: 275px;
    }

    &__step-text {
        color: $slate-03;
        font-size: 13px;
        line-height: 17.76px;
        margin-bottom: 10px;
        text-align: center;
    }

    &__text-big {
        font-size: 26px;
        line-height: 35.52px;
        font-weight: 600;
        text-align: center;
        margin-bottom: 5px;
    }

    &__text-small {
        text-align: center;
        font-size: 14px;
        line-height: 19.12px;
        color: $slate-03;
        margin-bottom: 22px;
    }

    &__step-container {
        width: 100%;
        background-color: color.mix(white, $gray-background, 20%);
    }

    &__confirmation {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 410px;
    }

    &__selected-exam-container {
        position: relative;
    }

    &__selected-exam {
        margin-bottom: 43px;
        cursor: auto;

        &:hover,
        &:focus {
            &::before {
                display: none;
            }
        }
    }

    &__unselect-exam-icon {
        position: absolute;
        top: 14px;
        right: 21px;
        color: $slate-01;
        cursor: pointer;

        &:hover {
            color: $brand-black;
        }
    }

    &__display-name-prompt {
        font-weight: 600;
        font-size: 15px;
        line-height: 20.49px;
        margin-bottom: 10px;
        padding-left: 11px;
    }

    &__display-name-hint {
        color: $slate-03;
        font-size: 14px;
        line-height: 19px;
        margin-bottom: 27px;
        padding-left: 11px;
    }

    &__display-name-input {
        margin-bottom: 41px;
    }

    &__mobile-warning {
        width: 100%;

        :deep(.mobile-screen-warning__message) {
            padding-right: 20px;
        }
    }
}
</style>
